import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Wand2, Upload, AlertCircle, Star, Calendar, FolderOpen, CheckCircle, ExternalLink, Copy, Check, Trash2 } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { DateTimePicker } from '../ui/DateTimePicker';
import { webhookService, projectService } from '../../services/api';
import { ArticleTypeId } from '../../types/index';

// Helper para usar proxy con URLs externas (evita errores HTTP2)
const getProxiedImageSrc = (src: string): string => {
    if (!src) return '';
    // Si ya es proxy, base64, o URL local, no modificar
    if (src.startsWith('/api/proxy-image') || src.startsWith('data:') || src.startsWith('/')) {
        return src;
    }
    // Si es URL externa (http/https), usar proxy
    if (src.startsWith('http')) {
        return `/api/proxy-image?url=${encodeURIComponent(src)}`;
    }
    return src;
};

interface WordPressOptimizerProps {
    articleContent: string;
    articleTitle: string;
    images: string[];
    projectDetails?: any;
    articleId?: string | number;
    projectId?: string;
    sessionId?: string;
    articleType?: string; // 'wikipedia' | 'comparative' | 'standard' | 'news'
    metaTitle?: string; // SEO meta title
    metaDescription?: string; // SEO meta description
    onClose: () => void;
    onSuccess: () => void;
}

interface ImageMetadata {
    id: string;
    src: string;
    alt: string;
    title: string;
    originalIndex: number;
    dataset?: {
        origin?: string;
        wpId?: string;
    };
}

interface WPCategory {
    id: number;
    name: string;
    slug: string;
}

interface SuccessResult {
    wordpress_post: {
        id: number;
        link: string;
        status: string;
        date: string;
    };
    images: Array<{
        key: string;
        url: string;
        id: number;
        alt: string;
        isExisting: boolean;
        isNew: boolean;
        isFeatured: boolean;
    }>;
    article_title: string;
}

export const WordPressOptimizer: React.FC<WordPressOptimizerProps> = ({
    articleContent,
    articleTitle,
    images,
    projectDetails,
    articleId,
    projectId,
    sessionId,
    articleType,
    metaTitle,
    metaDescription,
    onClose,
    onSuccess
}) => {
    const [imageList, setImageList] = useState<ImageMetadata[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [featuredImageId, setFeaturedImageId] = useState<string | null>(null); // State for featured image
    const [successResult, setSuccessResult] = useState<SuccessResult | null>(null); // State for success result
    const [copiedUrl, setCopiedUrl] = useState<string | null>(null); // State for copy feedback

    // Default date: Tomorrow at 09:00
    const [scheduledDate, setScheduledDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        // Format YYYY-MM-DDTHH:MM
        const pad = (n: number) => n < 10 ? '0' + n : n;
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
    });

    // Categories State
    const [categories, setCategories] = useState<WPCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [categoryError, setCategoryError] = useState('');

    // Article Type - usar directamente el prop (sin permitir cambios)
    const effectiveArticleType: ArticleTypeId = (articleType as ArticleTypeId) || 'wikipedia';

    useEffect(() => {
        // Parse articleContent to extract data-origin, data-wp-id, alt, and title from images
        const parser = new DOMParser();
        const doc = parser.parseFromString(articleContent || '', 'text/html');
        const imgElements = doc.querySelectorAll('img');

        // Create a map of src -> image metadata (including alt and title)
        const imageDataMap: Record<string, { origin?: string; wpId?: string; alt?: string; title?: string }> = {};
        imgElements.forEach((img) => {
            const src = img.getAttribute('src') || '';
            const origin = img.getAttribute('data-origin') || undefined;
            const wpId = img.getAttribute('data-wp-id') || undefined;
            const alt = img.getAttribute('alt') || undefined;
            const title = img.getAttribute('title') || undefined;
            if (src) {
                imageDataMap[src] = { origin, wpId, alt, title };
            }
        });

        // Initialize image list from props with dataset info AND existing metadata
        const initialImages = images.map((src, index) => {
            // Normalize src for lookup: decode HTML entities to match DOMParser output
            const decodedSrc = src.replace(/&amp;/g, '&');
            // Try both encoded and decoded versions for robust matching
            const imgData = imageDataMap[decodedSrc] || imageDataMap[src] || {};
            return {
                id: `imagen${index + 1}`,
                src,
                // Use existing alt/title from HTML if available, otherwise empty
                alt: imgData.alt || '',
                title: imgData.title || '',
                originalIndex: index,
                dataset: imgData.origin || imgData.wpId ? { origin: imgData.origin, wpId: imgData.wpId } : undefined
            };
        });

        setImageList(initialImages);

        // Auto-select first image as featured (it has the "Imagen Destacada" badge in the editor)
        if (initialImages.length > 0) {
            setFeaturedImageId(initialImages[0].id);
        }
    }, [images, articleContent]);

    // Fetch WordPress Categories
    useEffect(() => {
        // Resolve projectId: use prop first, then fallback to projectDetails.id
        const pid = projectId || (projectDetails && projectDetails.id);

        if (pid) {
            const fetchCats = async () => {
                setIsLoadingCategories(true);
                setCategoryError('');
                try {
                    const cats = await projectService.getWordPressCategories(pid);
                    setCategories(cats);
                } catch (err) {
                    setCategoryError('No se pudieron cargar las categorías desde WordPress.');
                } finally {
                    setIsLoadingCategories(false);
                }
            };
            fetchCats();
        }
    }, [projectId, projectDetails]);

    const handleMetadataChange = (index: number, field: 'alt' | 'title', value: string) => {
        const newImages = [...imageList];
        newImages[index] = { ...newImages[index], [field]: value };
        setImageList(newImages);
    };

    // Eliminar imagen del artículo
    const handleDeleteImage = (index: number) => {
        const imgToDelete = imageList[index];
        // Si es la imagen destacada, quitar la marca
        if (imgToDelete.id === featuredImageId) {
            setFeaturedImageId(null);
        }
        // Eliminar de la lista
        const newImages = imageList.filter((_, i) => i !== index);
        setImageList(newImages);
    };

    const generateSEOMetadata = (index: number) => {
        // Cleaning title for cleaner filenames/attributes
        const cleanTitle = articleTitle.replace(/[^\w\sñáéíóúü]/gi, '').trim();

        // Arrays for variety
        const altPrefixes = [
            "Vista detallada de", "Imagen representativa de", "Contexto visual para",
            "Ilustración sobre", "Gráfico explicativo de", "Concepto clave:",
            "Detalle visual de", "Referencia visual para"
        ];

        const titleSuffixes = [
            "img", "visual", "grafico", "ref", "foto", "archivo", "media"
        ];

        // Ensure uniqueness using modulo or random based on index
        const altPrefix = altPrefixes[index % altPrefixes.length];
        const titleSuffix = titleSuffixes[index % titleSuffixes.length];

        // Generate distinct Alt and Title
        // Alt: Descriptive sentence structure
        const suggestedAlt = `${altPrefix} ${cleanTitle} ${index + 1}`;

        // Title: Filename-like structure
        const suggestedTitle = `${cleanTitle}-${titleSuffix}-${index + 1}`;

        return { alt: suggestedAlt, title: suggestedTitle };
    };

    const suggestMetadata = (index: number) => {
        const img = imageList[index];
        const newImages = [...imageList];
        const { alt, title } = generateSEOMetadata(index);

        newImages[index] = {
            ...img,
            alt: img.alt || alt,
            title: img.title || title
        };
        setImageList(newImages);
    };

    const handleProcess = async () => {
        setIsProcessing(true);
        setError('');

        if (!scheduledDate) {
            setError('Por favor, selecciona una fecha y hora para programar la publicación.');
            setIsProcessing(false);
            return;
        }

        if (!selectedCategory) {
            setError('Por favor, selecciona una categoría para el artículo.');
            setIsProcessing(false);
            return;
        }

        // Validate Featured Image Selection
        if (imageList.length > 0 && !featuredImageId) {
            setError('Debes seleccionar una imagen destacada (haz clic en la estrella).');
            setIsProcessing(false);
            return;
        }

        try {
            // 0. AUTO-SAVE: Removed per user request
            /*
            if (articleId) {
                // ... removed
            }
            */

            // 1. Prepare HTML: Replace base64 images with placeholders
            // This is a simplified replacement. Real robust replacement might need parsing.
            // Assuming articleContent has            // 1. Prepare HTML: Replace base64 images with placeholders

            // SAFETY: Final cleaning of internal links to ensure valid scrolling
            let cleanContent = articleContent;

            // 1. Remove target="_blank" from any link with href="#"
            cleanContent = cleanContent.replace(/(<a\s+[^>]*?href="#[^"]*"[^>]*?)target="_blank"([^>]*?>)/gi, '$1$2');
            cleanContent = cleanContent.replace(/(<a\s+[^>]*?)target="_blank"([^>]*?href="#[^"]*"[^>]*?>)/gi, '$1$2');

            // 2. Remove rel="noopener noreferrer" from internal links
            cleanContent = cleanContent.replace(/(<a\s+[^>]*?href="#[^"]*"[^>]*?)rel="noopener noreferrer"([^>]*?>)/gi, '$1$2');
            cleanContent = cleanContent.replace(/(<a\s+[^>]*?)rel="noopener noreferrer"([^>]*?href="#[^"]*"[^>]*?>)/gi, '$1$2');

            // 3. REMOVER H1 existentes del contenido (WordPress añade el título automáticamente)
            // Esto evita títulos duplicados
            cleanContent = cleanContent.replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, '');

            // NO agregamos H1 manualmente - WordPress lo muestra como título del post
            let optimizedHtml = cleanContent;
            const imageMap: Record<string, any> = {};

            imageList.forEach((img) => {
                let payloadImage: any = {};

                // CRITICAL: Normalize HTML entities (sometimes src comes as "...&amp;id=...")
                const processingSrc = img.src.replace(/&amp;/g, '&');

                // 1. Detectar si es WP Media usando data-origin o heurísticas
                const isWpMedia = img.dataset?.origin === 'wp-media' ||
                    processingSrc.includes('/api/proxy-image') ||
                    processingSrc.match(/[?&]id=\d+/) ||
                    (processingSrc.startsWith('http') && !processingSrc.startsWith('data:'));

                let wpId: number | null = null;
                let cleanUrl = processingSrc;

                // Extraer ID de data-wp-id o de la URL
                if (img.dataset?.wpId) {
                    wpId = parseInt(img.dataset.wpId, 10);
                } else {
                    const idMatch = processingSrc.match(/[?&]id=(\d+)/);
                    if (idMatch) {
                        wpId = parseInt(idMatch[1], 10);
                    }
                }

                // Limpiar URL si es proxy
                if (processingSrc.includes('/api/proxy-image')) {
                    const urlMatch = processingSrc.match(/[?&]url=([^&]+)/);
                    if (urlMatch) {
                        cleanUrl = decodeURIComponent(urlMatch[1]);
                    }
                } else if (wpId) {
                    cleanUrl = processingSrc.split('?id=')[0];
                }

                if (isWpMedia && (wpId || cleanUrl.startsWith('http'))) {
                    // Ya está en WordPress -> solo referenciar, NO subir
                    payloadImage = {
                        type: 'media',
                        id: wpId,
                        url: cleanUrl,
                        alt: img.alt,
                        title: img.title,
                        destacada: img.id === featuredImageId ? 'si' : 'no'
                    };
                } else {
                    // Es base64 o nueva -> subir y optimizar
                    payloadImage = {
                        base64: img.src,
                        alt: img.alt,
                        title: img.title,
                        filename: `${(img.title || 'imagen').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.jpg`,
                        destacada: img.id === featuredImageId ? 'si' : 'no'
                    };
                }

                // Reemplazamos el src EXACTO actual por el placeholder
                optimizedHtml = optimizedHtml.split(img.src).join(`{{${img.id}}}`);

                // Añadimos al mapa
                imageMap[img.id] = payloadImage;
            });

            // Format Date strictly as YYYY-MM-DD HH:MM:SS
            let mysqlDate = null;
            if (scheduledDate) {
                // Ensure seconds are added if missing
                mysqlDate = scheduledDate.replace('T', ' ') + (scheduledDate.length === 16 ? ':00' : '');
            }

            // 2. Prepare Payload with Credentials if available
            const payload: any = {
                articleId: articleId, // Add articleId to payload as requested
                projectId: projectId, // Add projectId for robust credential lookup on backend
                html: optimizedHtml,
                images: imageMap,
                articleTitle,
                // SEO meta fields para plugins como Yoast, Rank Math, AIOSEO
                metaTitle: metaTitle || articleTitle,
                metaDescription: metaDescription || '',
                // Ya no enviamos imagen_destacada por separado, va indicado dentro de cada objeto de la lista images
                // Fecha programada con segundos (YYYY-MM-DD HH:MM:SS)
                fecha_programado: mysqlDate,
                session_id: sessionId,
                // Tipo de artículo para determinar flujo de optimización (usa el seleccionado por el usuario)
                articleType: effectiveArticleType
            };

            if (projectDetails) {
                payload.projectInfo = {
                    wordpress_url: projectDetails.wordpress_url,
                    wordpress_username: projectDetails.wordpress_username,
                    wordpress_password: projectDetails.wordpress_password
                };
            } else {
                // Credentials will be hydrated by backend if missing here
                // console.warn('⚠️ [WordPressOptimizer] No hay detalles del proyecto disponibles (se usarán del backend)');
            }

            // Always add Category Info if selected
            const catInfo = categories.find(c => String(c.id) === String(selectedCategory));
            if (catInfo) {
                payload.category_id = catInfo.id;
                payload.category_info = catInfo;
            }

            // 3. Send to Webhook
            const result = await webhookService.optimizeForWordPress(payload);

            // Store result for success view
            if (result && result.success) {
                setSuccessResult(result as SuccessResult);
            }

            // 4. Update Scheduled Date in Local Database
            if (articleId && mysqlDate) {
                try {
                    // Use format YYYY-MM-DD HH:MM:SS for MySQL
                    await projectService.updateArticleSchedule(Number(articleId), mysqlDate);
                } catch (dbError) {
                    // Error updating scheduled date in local DB
                }
            }

            // 5. Update Article Status AND Content in Local Database
            // RESTORED per user request: "me deberia aparecer ese mismo articulo pero con el html y titulo"
            // ALSO save article_type so it's remembered for next time
            if (articleId) {
                try {
                    await projectService.updateArticle(Number(articleId), {
                        title: articleTitle,
                        html: optimizedHtml, // Save the optimized HTML with placeholders
                        subid_wordpress: 'si',
                        // Guardar el tipo de artículo seleccionado para recordarlo la próxima vez
                        article_type: effectiveArticleType
                    });
                } catch (statusError) {
                    // Error updating article status/content
                }
            }

            // Don't close immediately - show success view with images
            // onSuccess will be called when user clicks "Cerrar" in success view

        } catch (error) {
            setError('Hubo un error al procesar el artículo. Por favor intenta de nuevo.');
        } finally {
            setIsProcessing(false);
        }
    };

    // Copy URL to clipboard
    const copyToClipboard = (url: string) => {
        navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        setTimeout(() => setCopiedUrl(null), 2000);
    };


    // Success View - Show after WordPress upload completes
    if (successResult) {
        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden shadow-2xl">
                    {/* Success Header */}
                    <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-7 h-7 text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    Artículo Enviado a WordPress
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {successResult.article_title}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Success Content */}
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                        <div className="space-y-6">
                            {/* WordPress Post Info */}
                            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-4">
                                    <ExternalLink className="w-4 h-4 text-blue-500" />
                                    Enlace del Artículo
                                </h3>
                                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                                    <input
                                        type="text"
                                        readOnly
                                        value={successResult.wordpress_post.link}
                                        className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                                    />
                                    <button
                                        onClick={() => copyToClipboard(successResult.wordpress_post.link)}
                                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                        title="Copiar URL"
                                    >
                                        {copiedUrl === successResult.wordpress_post.link ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Copy className="w-4 h-4 text-gray-500" />
                                        )}
                                    </button>
                                    <a
                                        href={successResult.wordpress_post.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                        title="Abrir en nueva pestaña"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                </div>
                                <div className="mt-3 flex gap-4 text-xs text-gray-500">
                                    <span>ID: {successResult.wordpress_post.id}</span>
                                    <span>Estado: {successResult.wordpress_post.status}</span>
                                    <span>Fecha: {new Date(successResult.wordpress_post.date).toLocaleString('es-ES')}</span>
                                </div>
                            </div>

                            {/* Images with URLs */}
                            {successResult.images && successResult.images.length > 0 && (
                                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                    <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-4">
                                        <ImageIcon className="w-4 h-4 text-purple-500" />
                                        Imágenes Procesadas ({successResult.images.length})
                                    </h3>
                                    <div className="space-y-4">
                                        {successResult.images.map((img, index) => (
                                            <div key={img.key} className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                                {/* Image Preview */}
                                                <div className="w-full md:w-40 aspect-video bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 relative">
                                                    <img
                                                        src={img.url}
                                                        alt={img.alt || `Imagen ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {img.isFeatured && (
                                                        <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                                            <Star className="w-3 h-3 fill-white" />
                                                            Destacada
                                                        </div>
                                                    )}
                                                    {img.isNew && (
                                                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                                                            Nueva
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Image Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                                            {img.key}
                                                        </span>
                                                        <span className="text-xs text-gray-400">ID: {img.id}</span>
                                                    </div>

                                                    {/* URL with copy */}
                                                    <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200">
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={img.url}
                                                            className="flex-1 text-xs text-gray-600 bg-transparent outline-none truncate"
                                                        />
                                                        <button
                                                            onClick={() => copyToClipboard(img.url)}
                                                            className="p-1.5 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                                                            title="Copiar URL"
                                                        >
                                                            {copiedUrl === img.url ? (
                                                                <Check className="w-3.5 h-3.5 text-green-600" />
                                                            ) : (
                                                                <Copy className="w-3.5 h-3.5 text-gray-400" />
                                                            )}
                                                        </button>
                                                        <a
                                                            href={img.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-1.5 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                                                            title="Abrir imagen"
                                                        >
                                                            <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                                                        </a>
                                                    </div>

                                                    {img.alt && (
                                                        <p className="mt-2 text-xs text-gray-500">
                                                            <span className="font-medium">Alt:</span> {img.alt}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Success Footer */}
                    <div className="p-6 border-t border-gray-100 bg-white">
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="primary"
                                onClick={onSuccess}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                icon={<CheckCircle className="w-4 h-4" />}
                            >
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden shadow-2xl">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <ImageIcon className="w-6 h-6 text-blue-600" />
                            Optimizar para WordPress
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">Configura el SEO de las imágenes antes de exportar</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={onClose}>
                            Cancelar
                        </Button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    <div className="space-y-6">
                        {/* Sección de Categoría */}
                        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
                                <FolderOpen className="w-4 h-4 text-orange-500" />
                                Categoría (Obligatorio)
                            </h3>
                            {isLoadingCategories ? (
                                <div className="text-sm text-gray-500 italic flex items-center gap-2">
                                    <div className="animate-spin h-4 w-4 border-2 border-orange-500 rounded-full border-t-transparent"></div>
                                    Cargando categorías de WordPress...
                                </div>
                            ) : categoryError ? (
                                <div className="text-sm text-red-500 bg-red-50 p-3 rounded-lg flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {categoryError}
                                </div>
                            ) : (
                                <div>
                                    <Select
                                        label="Selecciona una Categoría"
                                        required
                                        placeholder="-- Elige una categoría --"
                                        value={selectedCategory}
                                        onChange={(val) => setSelectedCategory(val as string)}
                                        options={categories.map(c => ({ value: c.id, label: c.name }))}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Sección de Programación */}
                        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
                                <Calendar className="w-4 h-4 text-purple-600" />
                                Programar Publicación (Obligatorio)
                            </h3>
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <div className="flex-1 w-full">
                                    <div className="flex-1 w-full relative z-10"> {/* z-10 for date picker popup */}
                                        <DateTimePicker
                                            label="Fecha y Hora de Publicación"
                                            required
                                            value={scheduledDate}
                                            onChange={setScheduledDate}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {imageList.map((img, index) => (
                            <div key={img.id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-6 items-start">
                                <div className="w-full md:w-48 aspect-video bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                                    <img src={getProxiedImageSrc(img.src)} alt={img.alt || 'Preview'} className="w-full h-full object-cover" />
                                </div>

                                <div className="flex-1 w-full space-y-4">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                {img.id}
                                            </span>
                                            {/* Botón de Imagen Destacada */}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setFeaturedImageId(img.id === featuredImageId ? null : img.id)}
                                                className={`h-8 gap-1.5 transition-colors ${featuredImageId === img.id
                                                    ? 'bg-yellow-50 text-yellow-600 border border-yellow-200'
                                                    : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'
                                                    }`}
                                                title={featuredImageId === img.id ? "Imagen Destacada" : "Marcar como Destacada"}
                                            >
                                                <Star className={`w-3.5 h-3.5 ${featuredImageId === img.id ? 'fill-yellow-500' : ''}`} />
                                                <span className="text-xs font-medium">
                                                    {featuredImageId === img.id ? 'Destacada' : 'Destacar'}
                                                </span>
                                            </Button>
                                            {/* Botón de Eliminar Imagen */}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteImage(index)}
                                                className="h-8 gap-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                title="Eliminar imagen del artículo"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                                <span className="text-xs font-medium">Eliminar</span>
                                            </Button>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => suggestMetadata(index)}
                                            className="text-blue-600 hover:bg-blue-50 h-8"
                                        >
                                            <Wand2 className="w-3 h-3 mr-1.5" /> Sugerir
                                        </Button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-medium text-gray-700">Texto Alternativo (Alt)</label>
                                            <input
                                                type="text"
                                                value={img.alt}
                                                onChange={(e) => handleMetadataChange(index, 'alt', e.target.value)}
                                                placeholder="Describe la imagen para SEO..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-xs font-medium text-gray-700">Título de Imagen</label>
                                            <input
                                                type="text"
                                                value={img.title}
                                                onChange={(e) => handleMetadataChange(index, 'title', e.target.value)}
                                                placeholder="Título para el archivo..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {imageList.length === 0 && (
                            <div className="text-center py-12 text-gray-500">
                                No se detectaron imágenes en el artículo para optimizar.
                            </div>
                        )}

                        {/* Spacer for DatePicker / Dropdowns */}
                        <div className="h-64"></div>
                    </div>
                </div>

                <div className="p-6 border-t border-gray-100 bg-white sticky bottom-0 z-10">
                    {error && (
                        <div className="mb-4 text-red-600 bg-red-50 p-3 rounded-lg flex items-center gap-2 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={onClose} disabled={isProcessing}>
                            Volver
                        </Button>
                        <Button
                            onClick={handleProcess}
                            isLoading={isProcessing}
                            disabled={imageList.length === 0}
                            className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200"
                            icon={<Upload className="w-4 h-4" />}
                        >
                            Procesar y Enviar a WordPress
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};
